import { Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag, Tooltip } from "@arco-design/web-react"
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom"
import { ItemReceiptAPI, VendorAPI } from "@/apis/purchasing";
import { Currency } from "@/components/Numeric";
import Address from "@/components/Address";
import SendEmailButton from "@/components/SendEmailButton";
import Reference from "@/components/Reference";
import GLImpactButton from "@/components/GLImpactButton";
import { FileUtil } from "@/utils";
import { POReceiptStatusDef, POReceiptStatus } from "@/enums/purchasing";
import { EnumLabel } from "@/components/Enum";

export const ReceiptInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [info, setInfo] = useState({});
    const [printLoading, setPrintLoading] = useState(false);
    const [inventoryInUse, setInventoryInUse] = useState(false);

    const getInfo = useCallback(async () => {
        const values = await ItemReceiptAPI.get(params.receiptId);
        setInfo(values);
        const inUse = await ItemReceiptAPI.checkInventoryInUse(params.receiptId);
        setInventoryInUse(inUse);
    }, [params]);

    const back = () => {
        navigate("/workspace/purchasing/receipt");
    }

    const deleteReceipt = async () => {
        try {
            await ItemReceiptAPI.delete(params.receiptId);
            Message.success("Item receipt deleted successfully");
            back();
        } catch (error) {
            Message.error(error.message);
        }
    }

    useEffect(() => {
        getInfo();
    }, [getInfo]);

    return <Card title={<Space>
        <span>{info.receiptNo}</span>
        <Tag><EnumLabel enumObj={POReceiptStatusDef} value={info.status} /></Tag>
    </Space>} extra={
        <Space>
            {info.status === POReceiptStatus.NotBilled &&
                <Tooltip content="Cannot modify because inventory has been picked, fulfilled, consumed, moved, or adjusted" disabled={!inventoryInUse}>
                    <span>
                        <Button type="primary" disabled={inventoryInUse} onClick={() => navigate(`/workspace/purchasing/receipt/${params.receiptId}/edit`)}>Edit</Button>
                    </span>
                </Tooltip>
            }
            {info.status === POReceiptStatus.NotBilled &&
                <Tooltip content="Cannot delete because inventory has been picked, fulfilled, consumed, moved, or adjusted" disabled={!inventoryInUse}>
                    <span>
                        <Popconfirm title="Are you sure you want to delete?"
                            onOk={() => deleteReceipt(params.receiptId)}>
                            <Button type="primary" status="danger" disabled={inventoryInUse}>Delete Item Receipt</Button>
                        </Popconfirm>
                    </span>
                </Tooltip>
            }
            {info.status !== POReceiptStatus.FullyBilled &&
                <Button type="primary" onClick={() => navigate("/workspace/purchasing/bills/receipt/" + params.receiptId)}>Create Bill</Button>
            }
            <GLImpactButton type="1" from={params.receiptId} />
            <Button type="primary" onClick={async () => {
                setPrintLoading(true);
                try {
                    const res = await ItemReceiptAPI.print(info.id);
                    FileUtil.download(res.data, `${info.poNo}.pdf`);
                } catch (error) {
                    Message.error(error.message);
                } finally {
                    setPrintLoading(false);
                }
            }} loading={printLoading}>Print</Button>
            <SendEmailButton getEmailContent={async () => {
                return await ItemReceiptAPI.getEmail(info.id);
            }} getToList={async () => {
                const vendor = await VendorAPI.get(info.vendorId);
                return vendor.contacts;
            }} sendEmail={async (val) => {
                await ItemReceiptAPI.sendEmail(info.id, val);
            }} />
            <Button onClick={() => back()}>Back</Button>
        </Space>
    }>
        <Descriptions colon=":"
            data={[{
                label: "Vendor",
                value: info.vendorName
            }, {
                label: "Drop Ship",
                value: info.dropShipName
            }, {
                label: "Ship to",
                value: <Address value={{
                    name: info.shipToContactName,
                    address: info.shipToAddress,
                    city: info.shipToCity,
                    state: info.shipToState,
                    zip: info.shipToZip
                }} inline />
            }, {
                label: "PO No",
                value: <Link to={`/workspace/purchasing/po/${info.poId}`}>{info.poNo}</Link>
            }, {
                label: "Receive Date",
                value: info.receiveDate
            }, {
                label: "Reference#",
                value: <Reference value={info.reference} />
            }, {
                label: "Warehouse Name",
                value: info.warehouseName
            }, {
                label: "Memo",
                value: info.memo
            }]} />

        <Table rowKey="id" columns={[
            { title: "Item Number", dataIndex: "itemNo" },
            { title: "Item Name", dataIndex: "itemName" },
            { title: "Description", dataIndex: "description" },
            { title: "Qty", dataIndex: "quantity" },
            { title: "U/M", dataIndex: "uom" },
            { title: "Rate", dataIndex: "rate" },
            { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },
            { title: "Location", dataIndex: "locationName" },
            { title: "Lot#", dataIndex: "lot" },
            { title: "Expiration Date", dataIndex: "expiredDate" },
        ]}
            data={info.items}
        />
    </Card>
}
